import { CSSProperties, useEffect, useRef, useState } from "react"

import type { SelectProps } from "~/shared/ui/kit"
import { InputField, Select, useInputField } from "~/shared/ui/kit"
import { MaterialIcon } from "~/shared/ui/kit/components/MaterialIcon"

export type SearchableSelectProps<C> = SelectProps<C> & {
	/** Вызывается при обновлении строки поиска */
	onSearch: (value: string | number | undefined) => void
	/** Выводится при пустой строке фильтра */
	searchPlaceholder?: string
}

const inputStyle = {
	left: "0",
	right: "0",
	top: "0",
	borderBottom: "none",
	position: "absolute",
	display: "none",
} as CSSProperties

const openInputStyle = {
	...inputStyle,
	display: "unset",
} as CSSProperties

const iconStyle = {
	padding: "0",
	alignItems: "center",
	justifyContent: "center",
	position: "absolute",
	right: "0",
	top: "0",
	width: "2.5rem",
	height: "2.5rem",
	display: "none",
} as CSSProperties

const openIconStyle = {
	...iconStyle,
	display: "flex",
} as CSSProperties

function Search({ onSearch, open, placeholder = "Фильтр..." }) {
	const [searchInput] = useInputField("")
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (open) {
			inputRef.current?.focus()
		}
	}, [open])

	useEffect(() => {
		if (open) {
			onSearch(searchInput.value)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchInput.value])

	return (
		<>
			<InputField
				ref={inputRef}
				placeholder={placeholder}
				style={open ? openInputStyle : inputStyle}
				{...searchInput}
			/>
			<div style={open ? openIconStyle : iconStyle}>
				<MaterialIcon name="search" />
			</div>
		</>
	)
}

/**
 * Расширение компонента [Select](/docs/shared-ui-kit-select) с добавлением поисковой строки
 * @component
 * @category Component
 */
export function SearchableSelect<C>({
	onSearch,
	searchPlaceholder,
	onOpen, onClose,
	...props
}: SearchableSelectProps<C>) {

	const [open, setOpen] = useState(false)

	const handleOpen = () => {
		onOpen?.()
		setOpen(true)
	}

	const handleClose = () => {
		onClose?.()
		setOpen(false)
	}

	return (
		<div style={{ position: "relative", width: "fit-content" }}>
			<Select {...props} onOpen={handleOpen} onClose={handleClose} />
			<Search
				onSearch={onSearch}
				open={open}
				placeholder={searchPlaceholder}
			/>
		</div>
	)
}
